import { Dictionary, isNil, isUndefined, forEach, isEmpty, isNull } from 'lodash';
import { Nullable, omitDeepUndefined } from '../../utils';
import { AssignmentEntry } from './AssignmentEntry';
import { PinEntryUpdateData } from './PinEntryUpdateData';

interface AssignmentEntryUpdateFields
  extends Partial<Pick<AssignmentEntry, 'displayName' | 'status' | 'totalPins' | 'startDate' | 'closeDate'>> {
  removeEntriesPinIds?: string[];
  removeAllPinEntries?: boolean;
  pinEntries?: PinEntryUpdateData[];
  seenAt?: number;
  activePinId?: string;
}
export type AssignmentEntryUpdateData = Nullable<AssignmentEntryUpdateFields>;

export type AssignmentEntryUnsetKeys = keyof Pick<
  AssignmentEntryUpdateFields,
  'displayName' | 'activePinId' | 'startDate' | 'closeDate'
>;

export namespace AssignmentEntryUpdateData {
  export function updateFields(data: AssignmentEntryUpdateData, unsetFields?: AssignmentEntryUnsetKeys[]): object {
    const fields: Dictionary<any> = {};
    const now = Date.now();

    if (!isNil(data.status)) fields['status'] = data.status;
    if (!isNil(data.seenAt)) fields[`seenAt`] = data.seenAt;
    if (!isNil(data.totalPins)) fields[`totalPins`] = data.totalPins;

    // nullable
    if (!isUndefined(data.displayName)) fields[`displayName`] = data.displayName;
    if (!isUndefined(data.activePinId)) fields[`activePinId`] = data.activePinId;
    if (!isUndefined(data.startDate)) fields['startDate'] = data.startDate;
    if (!isUndefined(data.closeDate)) fields['closeDate'] = data.closeDate;

    forEach(data.pinEntries, (pinEntry) => {
      const pinId = pinEntry.pinId;
      fields[`entries.${pinId}.pinId`] = pinEntry.pinId;
      fields[`entries.${pinId}.type`] = pinEntry.type;

      updateProperty(fields, `entries.${pinId}.done`, pinEntry.done);
      updateProperty(fields, `entries.${pinId}.result`, pinEntry.result);
      updateProperty(fields, `entries.${pinId}.answer`, pinEntry.answer);
      updateProperty(fields, `entries.${pinId}.resultType`, pinEntry.resultType);
    });

    if (data.removeAllPinEntries) {
      fields[`entries`] = {};
    }
    forEach(data.removeEntriesPinIds, (pinId) => (fields[`entries.${pinId}`] = null));

    const cleaned = omitDeepUndefined(fields);
    if (unsetFields) {
      unsetFields.forEach((key) => {
        if (key) cleaned[key] = null;
      });
    }
    if (!isEmpty(cleaned)) {
      cleaned['modifiedAt'] = now;
    }
    return cleaned;
  }
}

/** helper for setting the property, when the value is null. it will delete the field */
function updateProperty(updateFields: Dictionary<any>, field: string, value: any | null | undefined) {
  if (isNull(value)) {
    updateFields[field] = null;
  } else if (!isNil(value)) {
    updateFields[field] = value;
  }
}
